import { useState, useEffect } from "react";
import AddSSRItemModal from "./AddSSRItemModal";
import styles from "./SSRItemsTable.module.css";
import {
  useGetSSRCategories,
  useGetSSRItems,
  useCreateSSRItem,
  useUpdateSSRItem,
  useDeleteSSRItem,
} from "../../../../api/products/useSSRApi";
import { useToast } from "../../../../context/ToastContext";

const SSRItemsTable = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [apiError, setApiError] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: categories = [] } = useGetSSRCategories();
  const categoryIdForFilter = filterCategory
    ? categories.find((c) => c.code === filterCategory)?.id ?? ""
    : "";

  const { data: items = [], loading, error, refetch } = useGetSSRItems({
    page: 1,
    limit: 500,
    search: debouncedSearch,
    categoryId: categoryIdForFilter,
    status: filterStatus,
  });

  const { execute: createItem } = useCreateSSRItem();
  const { execute: updateItem } = useUpdateSSRItem();
  const { execute: deleteItem } = useDeleteSSRItem();

  const handleAddNew = () => {
    setEditingItem(null);
    setApiError(null);
    setIsAddModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setApiError(null);
    setIsAddModalOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm("Are you sure you want to remove this SSR item?")) {
      const result = await deleteItem(itemId);
      if (result.success) {
        showToast("SSR item removed successfully", "success");
        refetch();
      } else {
        showToast(result.message || "Failed to remove SSR item", "error");
      }
    }
  };

  const handleSaveItem = async (itemData) => {
    setApiError(null);
    if (editingItem) {
      const result = await updateItem(editingItem.id, itemData, categories);
      if (result.success) {
        showToast("SSR item updated successfully", "success");
        refetch();
        setIsAddModalOpen(false);
        setEditingItem(null);
        return;
      }
      showToast(result.message || "Failed to update SSR item", "error");
      setApiError(result.message);
      return result.message;
    }
    const result = await createItem(itemData, categories);
    if (result.success) {
      showToast("SSR item added successfully", "success");
      refetch();
      setIsAddModalOpen(false);
      return;
    }
    showToast(result.message || "Failed to add SSR item", "error");
    setApiError(result.message);
    return result.message;
  };

  const getCategoryInfo = (categoryCode) => {
    const category = categories.find((c) => c.code === categoryCode);
    return category || { name: categoryCode, icon: "📋", color: "#6b7280" };
  };

  const activeCount = items.filter((item) => item.status === "active").length;
  const inactiveCount = items.filter((item) => item.status === "inactive").length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>SSR Items</h2>
          <p className={styles.subtitle}>
            Manage special service request items and configurations
          </p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.exportBtn}>📥 Export</button>
          <button className={styles.refreshBtn} onClick={() => refetch()}>🔄 Refresh</button>
          <button className={styles.addBtn} onClick={handleAddNew}>
            + Add SSR Item
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by code or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.code} value={cat.code}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Stats */}
      <div className={styles.statsBar}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total Items:</span>
          <span className={styles.statValue}>{items.length}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Active:</span>
          <span className={`${styles.statValue} ${styles.activeText}`}>
            {activeCount}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Inactive:</span>
          <span className={`${styles.statValue} ${styles.inactiveText}`}>
            {inactiveCount}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Showing:</span>
          <span className={styles.statValue}>{items.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>SSR CODE</th>
              <th>DESCRIPTION</th>
              <th>CATEGORY</th>
              <th>MAX WEIGHT</th>
              <th>MAX SIZE</th>
              <th>MAX PIECES</th>
              <th>DIRECTION</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className={styles.emptyState}>
                  <p>Loading SSR items…</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="9" className={styles.emptyState}>
                  <div className={styles.emptyIcon}>🔍</div>
                  <p>No SSR items found</p>
                  <span>Try adjusting your filters</span>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const categoryInfo = getCategoryInfo(item.category);
                return (
                  <tr key={item.id}>
                    <td>
                      <span className={styles.ssrCode}>{item.code}</span>
                    </td>
                    <td>
                      <div className={styles.description}>
                        {item.description}
                      </div>
                    </td>
                    <td>
                      <div className={styles.category}>
                        <span
                          className={styles.categoryIcon}
                          style={{ background: categoryInfo.color }}
                        >
                          {categoryInfo.icon}
                        </span>
                        <span>{categoryInfo.name}</span>
                      </div>
                    </td>
                    <td>
                      {item.maxWeight ? (
                        <span className={styles.constraint}>
                          {item.maxWeight} kg
                        </span>
                      ) : (
                        <span className={styles.na}>—</span>
                      )}
                    </td>
                    <td>
                      {item.maxSize ? (
                        <span className={styles.constraint}>{item.maxSize}</span>
                      ) : (
                        <span className={styles.na}>—</span>
                      )}
                    </td>
                    <td>
                      {item.maxPieces ? (
                        <span className={styles.constraint}>
                          {item.maxPieces}
                        </span>
                      ) : (
                        <span className={styles.na}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.direction}>{item.direction}</span>
                    </td>
                    <td>
                      <span
                        className={`${styles.status} ${
                          item.status === "active"
                            ? styles.statusActive
                            : styles.statusInactive
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button onClick={() => handleEdit(item)}>Edit</button>
                        <button className={styles.removeBtn} onClick={() => handleDelete(item.id)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AddSSRItemModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingItem(null);
          setApiError(null);
        }}
        onSave={handleSaveItem}
        editingItem={editingItem}
        categories={categories}
        apiError={apiError}
      />
    </div>
  );
};

export default SSRItemsTable;